/**
 * Give credit (docs/credit.md, flag `giveCreditUI`; owner, 2026-10-09: "When a job is done, there
 * should be a quiet way to give the credit to another peon"). On a done task the peon who holds the
 * credit (`done_by`) or a steward hands the completion and its gold, as paid, to another peon, with no
 * push and no line from the peon. The words both clients and the server use, and the one rule the
 * clients need to offer the names. Who may do it is shared/permissions.ts `canGiveCredit`; what moves is
 * server/db.ts `creditTask`. Pure, so the Node tests import it by extension.
 */

/** The written words. Plain, as the app speaks; nothing here is said aloud. */
export const CREDIT_WORDS = {
  /** The quiet word at the done task's foot, above Reopen. */
  button: "Give credit",
  /** The names row's name, read as the group's label (the Nudge row's group is named the same way). */
  heading: "Who did it?",
  /** The confirm's yes, in the app's alert (the web's browser dialog keeps its own OK). */
  ok: "Give it",
  /** The confirm's no, in the app's alert (the web's browser dialog keeps its own Cancel). */
  cancel: "Keep it",
} as const;

/** The confirm's question: "Give credit to Hue? The gold goes with it." */
export function creditQuestion(to: string): string {
  return `Give credit to ${to}? The gold goes with it.`;
}

/** The 403 when the caller neither holds the credit nor is a steward: "Only Hue or a steward can give this credit." */
export function creditRefusal(holder: string | null | undefined): string {
  return holder ? `Only ${holder} or a steward can give this credit.` : "Only a steward can give this credit.";
}

/** The 409 for a task that is not done (reopened meanwhile, or never finished). */
export const CREDIT_NOT_DONE = "This task isn't done.";

/** The 409 when the name picked already holds the credit: "Hue already has the credit." */
export function creditAlreadyHeld(holder: string): string {
  return `${holder} already has the credit.`;
}

/** The 400 when the name picked is none of the household's. */
export const CREDIT_UNKNOWN_NAME = "Nobody here by that name.";

/** The purse's muted word on the holder's line when the credit left them: "credit given to Hue". */
export function creditGivenNote(to: string | null | undefined): string {
  const name = typeof to === "string" ? to.trim() : "";
  return name ? `credit given to ${name}` : "credit given";
}

/** The purse's muted word on the new peon's line: "credit from Omie". */
export function creditFromNote(from: string): string {
  return `credit from ${from.trim()}`;
}

/** Two spellings of one name: the same after trimming, collapsing spaces and ignoring case. */
export function sameCreditName(a: string | null | undefined, b: string | null | undefined): boolean {
  const key = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return typeof a === "string" && typeof b === "string" && key(a) === key(b);
}

/** The names the credit can go to: every household name but the holder's, in the list's order. */
export function creditNames(names: readonly string[], holder: string | null | undefined): string[] {
  return names.filter((n) => !sameCreditName(n, holder));
}

/**
 * The registered spelling of `requested` among the household's names: the exact spelling first, then
 * the one matching it ignoring case and spacing; null when it is none of them.
 */
export function registeredName(names: readonly string[], requested: string): string | null {
  return names.find((n) => n === requested) ?? names.find((n) => sameCreditName(n, requested)) ?? null;
}
